/**
 * VerifyPanel — Phase 3: Data verification & constraint editing.
 * Editable address table with lock status, technician review tab,
 * validation checks, and readiness gates.
 */

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { validateAddresses, validateWorkers } from '../utils/validationRules';
import * as api from '../services/routeOptimizerService';
import DataTable from './DataTable';
import ChangeLockIndicator from './ChangeLockIndicator';

const VERIFY_ADDRESS_COLUMNS = [
  { key: 'customer_id', label: 'ID', width: '70px' },
  { key: 'name', label: 'Name', editable: true },
  { key: 'address', label: 'Address', editable: true },
  { key: 'city', label: 'City', editable: true },
  { key: 'zip', label: 'Zip', editable: true, width: '70px' },
  { key: 'preferred_technician', label: 'Pref Tech', editable: true },
  { key: 'required_days', label: 'Req Days', editable: true },
  { key: 'minimum_qualification', label: 'Min Qual', editable: true, width: '80px' },
  { key: 'service_tier', label: 'Tier', editable: true, width: '80px' },
];

const VERIFY_WORKER_COLUMNS = [
  { key: 'tech_id', label: 'ID', width: '70px' },
  { key: 'name', label: 'Name', editable: true },
  {
    key: 'qualification', label: 'Qualification', editable: true, width: '120px',
    render: (row) => {
      const q = (row.qualification || row.qual || 'ST').toUpperCase();
      return <span className={`ro-qual-badge ro-qual-${q.toLowerCase()}`}>{q}</span>;
    },
  },
  { key: 'start_location', label: 'Start Location', editable: true },
  { key: 'home_address', label: 'Home Address', editable: true },
  { key: 'days_available', label: 'Days Available', editable: true },
];

export default function VerifyPanel({ weekId, details, onRefresh, onProceed }) {
  const addresses = details.addresses || [];
  const workers = details.workers || [];
  const addrCount = details.address_count || addresses.length;
  const workerCount = details.worker_count || workers.length;
  const [delta, setDelta] = useState(null);
  const [activeTab, setActiveTab] = useState('addresses');

  // Load data delta on mount
  useEffect(() => {
    if (!weekId) return;
    api.getDataDelta(weekId).then(setDelta).catch(() => {});
  }, [weekId]);

  // Run validation
  const settings = details.settings || {};
  const addrValidation = useMemo(() => validateAddresses(addresses, workers, settings), [addresses, workers, settings]);
  const workerValidation = useMemo(() => validateWorkers(workers), [workers]);

  const totalErrors = addrValidation.errors.length + workerValidation.errors.length;
  const totalWarnings = addrValidation.warnings.length + workerValidation.warnings.length;

  // Constraint summary
  const withPrefTech = addresses.filter(a => a.preferred_technician || a.pref_tech).length;
  const withReqDays = addresses.filter(a => {
    const rd = a.required_days || a.req_days;
    return rd && (Array.isArray(rd) ? rd.length > 0 : String(rd).trim().length > 0);
  }).length;
  const withMinQual = addresses.filter(a => a.minimum_qualification || a.min_qual).length;

  // Worker qualifications
  const qualCounts = {};
  workers.forEach(w => {
    const q = (w.qualification || w.qual || 'ST').toUpperCase();
    qualCounts[q] = (qualCounts[q] || 0) + 1;
  });

  // Geocoding check
  const missingCoords = addresses.filter(a => !a.lat && !a.lon).length;
  const allGeocoded = missingCoords === 0 && addrCount > 0;

  // Worker availability & home-start checks
  const limitedAvailWorkers = workers.filter(w => {
    const da = w.days_available;
    if (!da) return false;
    const days = String(da).split(',').map(d => d.trim()).filter(Boolean);
    return days.length > 0 && days.length < 3;
  }).length;

  const homeMissingAddress = workers.filter(w => {
    const sl = (w.start_location || '').toLowerCase();
    return sl === 'home' && !(w.home_address || '').trim();
  }).length;

  // Readiness checks
  const checks = [
    { label: 'Addresses uploaded', pass: addrCount > 0, value: `${addrCount} addresses` },
    { label: 'Workers uploaded', pass: workerCount > 0, value: `${workerCount} workers` },
    { label: 'All addresses geocoded', pass: allGeocoded, value: allGeocoded ? 'All geocoded' : `${missingCoords} missing coordinates` },
    { label: 'No validation errors', pass: totalErrors === 0, value: totalErrors === 0 ? 'All clear' : `${totalErrors} errors` },
    { label: 'Distance matrix', pass: details.has_distance_matrix, value: details.has_distance_matrix ? 'Ready' : 'Will auto-build' },
  ];

  const canProceed = addrCount > 0 && workerCount > 0 && totalErrors === 0 && allGeocoded && homeMissingAddress === 0;

  // Build error lookup for DataTable row highlighting
  const addrErrorRows = useMemo(() => {
    const m = new Map();
    addrValidation.errors.forEach(e => { if (e.row >= 0) m.set(e.row, 'error'); });
    addrValidation.warnings.forEach(w => { if (w.row >= 0 && !m.has(w.row)) m.set(w.row, 'warning'); });
    return m;
  }, [addrValidation]);

  const addrRowClass = (row, index) => {
    const level = addrErrorRows.get(index);
    if (level === 'error') return 'ro-dt-row-error';
    if (level === 'warning') return 'ro-dt-row-warning';
    return '';
  };

  // Normalize addresses with last_change_date for lock indicator
  const normalized = addresses.map(a => ({
    customer_id: a.customer_id || a.id || '',
    name: a.name || a.customer_name || '',
    address: a.address || a.street || '',
    city: a.city || '',
    zip: a.zip || a.zipcode || '',
    preferred_technician: a.preferred_technician || a.pref_tech || '',
    required_days: a.required_days || a.req_days || '',
    minimum_qualification: a.minimum_qualification || a.min_qual || '',
    service_tier: a.service_tier || a.tier || '',
    last_change_date: a.last_change_date || '',
  }));

  // Normalize workers
  const normalizedWorkers = workers.map(w => ({
    tech_id: w.tech_id || w.id || '',
    name: w.name || '',
    qualification: w.qualification || w.qual || 'ST',
    start_location: w.start_location || '',
    home_address: w.home_address || '',
    days_available: w.days_available || '',
  }));

  // Inline editing handlers
  const handleAddressEdit = useCallback((index, key, value) => {
    api.updateAddresses(weekId, [{ index, [key]: value }]).then(onRefresh).catch(() => {});
  }, [weekId, onRefresh]);

  const handleWorkerEdit = useCallback((index, key, value) => {
    api.updateWorkers(weekId, [{ index, [key]: value }]).then(onRefresh).catch(() => {});
  }, [weekId, onRefresh]);

  const handleLockOverride = useCallback((index) => {
    api.overrideLock(weekId, index).then(onRefresh).catch(() => {});
  }, [weekId, onRefresh]);

  const handleAddressBulkEdit = useCallback((indices, key, value) => {
    const updates = indices.map(index => ({ index, [key]: value }));
    api.updateAddresses(weekId, updates).then(onRefresh).catch(() => {});
  }, [weekId, onRefresh]);

  const handleWorkerBulkEdit = useCallback((indices, key, value) => {
    const updates = indices.map(index => ({ index, [key]: value }));
    api.updateWorkers(weekId, updates).then(onRefresh).catch(() => {});
  }, [weekId, onRefresh]);

  // Lock column
  const lockColumn = useMemo(() => [{
    key: '_lock',
    label: 'Lock',
    width: '50px',
    render: (row, originalIndex) => (
      <ChangeLockIndicator
        lastChangeDate={row.last_change_date}
        onOverride={() => handleLockOverride(originalIndex)}
      />
    ),
  }], [handleLockOverride]);

  // Column filters for address table
  const verifyColumnFilters = useMemo(() => {
    const tiers = [...new Set(normalized.map(a => a.service_tier).filter(Boolean))].sort();
    const days = [...new Set(normalized.map(a => a.required_days).filter(Boolean))].sort();
    const filters = [];
    if (tiers.length > 1) {
      filters.push({
        key: 'service_tier', label: 'Tier',
        options: tiers.map(t => ({ value: t, label: t })),
      });
    }
    if (days.length > 0) {
      filters.push({
        key: 'required_days', label: 'Fixed Day',
        options: days.map(d => ({ value: d, label: d })),
      });
    }
    filters.push({
      key: '_lock_status', label: 'Lock',
      options: [{ value: 'locked', label: 'Locked' }, { value: 'unlocked', label: 'Unlocked' }],
      filterFn: (row, val) => {
        const hasLock = row.last_change_date && (Date.now() - new Date(row.last_change_date).getTime()) < 90 * 24 * 60 * 60 * 1000;
        return val === 'locked' ? !!hasLock : !hasLock;
      },
    });
    if (totalErrors > 0 || totalWarnings > 0) {
      filters.push({
        key: '_issues', label: 'Issues',
        options: [
          { value: 'errors', label: 'Has Errors' },
          { value: 'warnings', label: 'Has Warnings' },
          { value: 'clean', label: 'Clean' },
        ],
        filterFn: (row, val) => {
          const idx = normalized.indexOf(row);
          const level = addrErrorRows.get(idx);
          if (val === 'errors') return level === 'error';
          if (val === 'warnings') return level === 'warning';
          return !level;
        },
      });
    }
    return filters;
  }, [normalized, totalErrors, totalWarnings, addrErrorRows]);

  return (
    <div className="ro-panel">
      <h3>Data Verification & Constraint Editing</h3>

      {/* Delta summary vs previous approved run */}
      {delta?.has_previous && (
        <div className="ro-tag-row" style={{ marginBottom: '12px' }}>
          {delta.new_addresses > 0 && (
            <span className="ro-tag ro-tag-success">+{delta.new_addresses} new</span>
          )}
          {delta.removed_addresses > 0 && (
            <span className="ro-tag ro-tag-danger">-{delta.removed_addresses} removed</span>
          )}
          {delta.changed_addresses > 0 && (
            <span className="ro-tag ro-tag-warning">{delta.changed_addresses} changed</span>
          )}
          {delta.locked_count > 0 && (
            <span className="ro-tag">{delta.locked_count} locked</span>
          )}
          {delta.new_workers > 0 && (
            <span className="ro-tag ro-tag-success">+{delta.new_workers} new tech{delta.new_workers !== 1 ? 's' : ''}</span>
          )}
          {delta.removed_workers > 0 && (
            <span className="ro-tag ro-tag-danger">-{delta.removed_workers} removed tech{delta.removed_workers !== 1 ? 's' : ''}</span>
          )}
          {delta.new_addresses === 0 && delta.removed_addresses === 0 && delta.changed_addresses === 0
            && !delta.new_workers && !delta.removed_workers && (
            <span className="ro-tag">No changes vs previous approval</span>
          )}
        </div>
      )}

      {/* Summary cards */}
      <div className="ro-summary-grid">
        <SummaryCard value={addrCount} label="Addresses" color="var(--ro-blue)" />
        <SummaryCard value={workerCount} label="Workers" color="var(--ro-blue)" />
        <SummaryCard value={withPrefTech} label="Fixed Tech" color={withPrefTech > 0 ? 'var(--ro-orange)' : 'var(--ro-border-light)'} />
        <SummaryCard value={withReqDays} label="Fixed Day" color={withReqDays > 0 ? 'var(--ro-orange)' : 'var(--ro-border-light)'} />
        <SummaryCard value={withMinQual} label="Min Qual" color={withMinQual > 0 ? 'var(--ro-orange)' : 'var(--ro-border-light)'} />
      </div>

      {/* Validation results */}
      {totalErrors === 0 && totalWarnings === 0 && addrCount > 0 && (
        <div className="ro-validation-success">
          <strong>All clear</strong>
          <span> — no validation issues found.</span>
        </div>
      )}
      {(totalErrors > 0 || totalWarnings > 0) && (
        <div className="ro-section">
          <h4 className="ro-subsection-title">Validation Results</h4>
          {totalErrors > 0 && (
            <div className="ro-validation-error">
              <strong>{totalErrors} error(s)</strong>
              <span> — must be fixed before proceeding</span>
              <ul>
                {groupByMessage([...addrValidation.errors, ...workerValidation.errors]).map(([msg, rows], i) => (
                  <li key={i}>
                    {rows.length > 1 ? `${rows.length}\u00D7 ` : rows.length === 1 && rows[0] >= 0 ? `Row ${rows[0] + 1}: ` : ''}
                    {msg}
                    {rows.length > 1 && <span className="ro-validation-rows"> (rows {rows.filter(r => r >= 0).slice(0, 8).map(r => r + 1).join(', ')}{rows.length > 8 ? ', ...' : ''})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {totalWarnings > 0 && (
            <div className="ro-validation-warning">
              <strong>{totalWarnings} warning(s)</strong>
              <ul>
                {groupByMessage([...addrValidation.warnings, ...workerValidation.warnings]).map(([msg, rows], i) => (
                  <li key={i}>
                    {rows.length > 1 ? `${rows.length}\u00D7 ` : rows.length === 1 && rows[0] >= 0 ? `Row ${rows[0] + 1}: ` : ''}
                    {msg}
                    {rows.length > 1 && <span className="ro-validation-rows"> (rows {rows.filter(r => r >= 0).slice(0, 8).map(r => r + 1).join(', ')}{rows.length > 8 ? ', ...' : ''})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Worker-specific checks */}
      {homeMissingAddress > 0 && (
        <div className="ro-validation-error">
          <strong>{homeMissingAddress} worker(s) set to start from Home but missing home address</strong>
          <span> — must be fixed before proceeding</span>
        </div>
      )}
      {limitedAvailWorkers > 0 && (
        <div className="ro-validation-warning">
          <strong>{limitedAvailWorkers} worker(s) have limited availability (fewer than 3 days)</strong>
        </div>
      )}
      {delta?.locked_count > 0 && addrCount > 0 && (delta.locked_count / addrCount) >= 0.8 && (
        <div className="ro-validation-warning">
          <strong>{delta.locked_count} of {addrCount} addresses ({Math.round(delta.locked_count / addrCount * 100)}%) are within the change lock window</strong>
          <span> — reassignments will be limited; consider extending the lock period or waiting for locks to expire</span>
        </div>
      )}

      {/* Readiness checks */}
      <div className="ro-section">
        <h4 className="ro-subsection-title">Readiness Checks</h4>
        <div className="ro-check-list">
          {checks.map((c, i) => (
            <div key={i} className={`ro-check-item ${c.pass ? '' : 'ro-check-item-fail'}`} style={{ animationDelay: `${i * 0.06}s` }}>
              <span className={`ro-check-icon ${c.pass ? 'ro-check-icon-pass' : 'ro-check-icon-fail'}`}>
                {c.pass ? '\u2713' : '\u2717'}
              </span>
              <span className="ro-check-label">{c.label}</span>
              <span className="ro-check-value">{c.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Data tabs: Addresses | Employees */}
      {(addrCount > 0 || workerCount > 0) && (
        <div className="ro-section">
          <div className="ro-tabs" style={{ marginBottom: '12px' }}>
            <button
              className={`ro-tab ${activeTab === 'addresses' ? 'ro-tab-active' : ''}`}
              onClick={() => setActiveTab('addresses')}
            >
              Addresses ({addrCount})
            </button>
            <button
              className={`ro-tab ${activeTab === 'technicians' ? 'ro-tab-active' : ''}`}
              onClick={() => setActiveTab('technicians')}
            >
              Employees ({workerCount})
            </button>
          </div>

          {activeTab === 'addresses' && addrCount > 0 && (
            <>
              <h4 className="ro-subsection-title">
                Address Data{(totalErrors > 0 || totalWarnings > 0) ? ' (issues highlighted — double-click to edit)' : ` (${addrCount} rows — double-click to edit)`}
              </h4>
              <DataTable
                columns={VERIFY_ADDRESS_COLUMNS}
                data={normalized}
                pageSize={50}
                onEdit={handleAddressEdit}
                extraColumns={lockColumn}
                selectable
                onBulkEdit={handleAddressBulkEdit}
                rowClassName={addrRowClass}
                columnFilters={verifyColumnFilters}
              />
            </>
          )}

          {activeTab === 'technicians' && workerCount > 0 && (
            <>
              <h4 className="ro-subsection-title">Worker Qualifications</h4>
              <div className="ro-tag-row" style={{ marginBottom: '12px' }}>
                {Object.entries(qualCounts).map(([qual, count]) => (
                  <span key={qual} className={`ro-qual-badge ro-qual-badge-lg ro-qual-${qual.toLowerCase()}`}>
                    {qual}: {count}
                  </span>
                ))}
              </div>
              <h4 className="ro-subsection-title">
                Employee Data ({workerCount} rows — double-click to edit)
              </h4>
              <DataTable
                columns={VERIFY_WORKER_COLUMNS}
                data={normalizedWorkers}
                pageSize={25}
                onEdit={handleWorkerEdit}
                selectable
                onBulkEdit={handleWorkerBulkEdit}
              />
            </>
          )}
        </div>
      )}

      {/* Proceed button */}
      <div className="ro-section-divider">
        <button
          className="ro-btn ro-btn-primary"
          onClick={onProceed}
          disabled={!canProceed}
        >
          {canProceed
            ? 'Proceed to Settings'
            : addrCount === 0 || workerCount === 0
              ? 'Upload addresses and workers first'
              : !allGeocoded
                ? `${missingCoords} address(es) need geocoding`
                : homeMissingAddress > 0
                  ? `${homeMissingAddress} worker(s) missing home address`
                  : totalErrors > 0
                    ? `Fix ${totalErrors} error(s) first`
                    : 'Upload addresses and workers first'}
        </button>
        {canProceed && (
          <span className="ro-proceed-hint">All checks passed</span>
        )}
      </div>
    </div>
  );
}

function groupByMessage(items) {
  const map = new Map();
  for (const item of items) {
    const rows = map.get(item.message) || [];
    rows.push(item.row);
    map.set(item.message, rows);
  }
  return [...map.entries()];
}

function SummaryCard({ value, label, color = 'var(--ro-blue)' }) {
  return (
    <div className="ro-card ro-summary-card" style={{ borderTop: `3px solid ${color}` }}>
      <div className="ro-summary-card-value" style={{ color }}>{value}</div>
      <div className="ro-summary-card-label">{label}</div>
    </div>
  );
}
